import { DEFAULT_COLOR } from 'src/config';

export interface FlowParticlePoint {
  x: number;
  y: number;
}

export interface FlowParticleConnector {
  id: string;
  color?: string;
  flowEnabled?: boolean;
  flowSpeed?: number;
  flowParticleCount?: number;
  flowParticleSize?: number;
  flowDirection?: 'forward' | 'backward' | 'bidirectional';
  path?: {
    tiles?: FlowParticlePoint[];
  };
}

export interface FlowParticle {
  id: string;
  x: number;
  y: number;
  size: number;
  opacity: number;
  color: string;
  dx: number;
  dy: number;
}

export const FLOW_ANIMATION_DURATION_MS = 3000;

/**
 * Calculate connector particles for an exact point in time. Keeping this
 * function pure makes live playback and frame-by-frame exports identical.
 */
export const calculateFlowParticles = (
  connector: FlowParticleConnector,
  timeMs: number
): FlowParticle[] => {
  if (!connector.flowEnabled) return [];

  const path = connector.path?.tiles;
  if (!path || path.length < 2) return [];

  const particles: FlowParticle[] = [];
  const particleCount = connector.flowParticleCount || 5;
  const speed = connector.flowSpeed || 1;
  const direction = connector.flowDirection || 'forward';
  const time = (timeMs * speed) / FLOW_ANIMATION_DURATION_MS;

  for (let i = 0; i < particleCount; i++) {
    const offset = i / particleCount;
    let progress = (time + offset) % 1;

    if (direction === 'backward') {
      progress = 1 - progress;
    } else if (direction === 'bidirectional') {
      progress =
        i < particleCount / 2
          ? (time + offset) % 1
          : 1 - ((time + offset) % 1);
    }

    const edgeThreshold = 0.05;
    if (progress < edgeThreshold || progress > 1 - edgeThreshold) continue;

    const segmentProgress = progress * (path.length - 1);
    const segmentIndex = Math.floor(segmentProgress);
    const segmentT = segmentProgress - segmentIndex;
    const fromIndex = Math.min(segmentIndex, path.length - 2);
    const toIndex = Math.min(segmentIndex + 1, path.length - 1);
    const from = path[fromIndex];
    const to = path[toIndex];

    let opacity = 1;
    const fadeZone = 0.15;
    if (progress < edgeThreshold + fadeZone) {
      opacity = (progress - edgeThreshold) / fadeZone;
    } else if (progress > 1 - edgeThreshold - fadeZone) {
      opacity = (1 - edgeThreshold - progress) / fadeZone;
    }

    particles.push({
      id: `particle-${connector.id}-${i}`,
      x: from.x + (to.x - from.x) * segmentT,
      y: from.y + (to.y - from.y) * segmentT,
      size: connector.flowParticleSize || 8,
      opacity,
      color: connector.color || DEFAULT_COLOR.value,
      dx: to.x - from.x,
      dy: to.y - from.y
    });
  }

  return particles;
};
